'use client';

import React, { useMemo } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useGetStrategyMetricsQuery } from 'src/api/redux/strategy';
import { useGetMyChainedStrategiesQuery } from 'src/api/redux';
import { useMetricToggles } from 'src/hooks/strategy';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { LOCATION } from 'src/hooks/filters/constants';
import {
  getTargetStrategyIdString,
  resolveEncountersDisplayOptions,
} from 'src/utils/strategyHelpers';

const MapMetricToggle = () => {
  const { selectedChainIds } = useStrategies(LOCATION.STRATEGY_CENTER);
  const { showEncountersTotals } = useFlags();
  const { data: chainedStrategies } = useGetMyChainedStrategiesQuery();
  const selectedChain = useMemo(() => {
    if (selectedChainIds.length !== 1) return null;
    return chainedStrategies?.results?.find(
      (s: any) => s.id === selectedChainIds[0]
    ) ?? null;
  }, [selectedChainIds, chainedStrategies?.results]);
  const strategyId = getTargetStrategyIdString(selectedChain);

  const { currentData: strategyMetrics } = useGetStrategyMetricsQuery(
    { strategyType: 'target', strategyId },
    { skip: !strategyId }
  );

  const { metricToggles, setMetricToggle } = useMetricToggles();

  const firstMetric = strategyMetrics?.results?.[0];
  const metricId = firstMetric?.id?.toString();
  // The "Claims" option reads encounters when the flag is on, so it is labeled
  // "Encounters" to match; the standalone encounters option is never rendered.
  const displayOptions = resolveEncountersDisplayOptions<{
    code: string;
    label: string;
  }>(firstMetric?.display_options, !!showEncountersTotals);

  if (!displayOptions || displayOptions.length < 2 || !metricId) {
    return null;
  }

  const activeOption =
    metricToggles[metricId] || displayOptions[0]?.code;

  const handleToggleClick = (code: string) => {
    if (!metricId) return;
    setMetricToggle(metricId, code);
  };

  return (
    <div
      className="absolute top-5 right-5 z-40 bg-white rounded-lg shadow-[0px_8px_8px_0px_rgba(0,0,0,0.08)] border border-neutral-200 px-3 py-2"
      data-testid="map-metric-toggle"
      aria-label="Map metric display toggle"
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-neutral-700 whitespace-nowrap">
          {firstMetric.label}
        </span>
        <div className="flex items-center gap-1">
          {displayOptions.map((option, index) => (
            <React.Fragment key={option.code}>
              <button
                type="button"
                className={`text-xs capitalize cursor-pointer px-1 py-0.5 rounded transition-colors ${
                  activeOption === option.code
                    ? 'text-sky-600 font-semibold'
                    : 'text-neutral-600 hover:bg-neutral-100'
                }`}
                onClick={() => handleToggleClick(option.code)}
                aria-pressed={activeOption === option.code}
                data-testid={`map-metric-toggle-${option.code}`}
              >
                {option.label}
              </button>
              {index < displayOptions.length - 1 && (
                <Icon
                  icon={faCircle}
                  className="text-slate-200 text-[0.35rem]"
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MapMetricToggle;
